import type {MRT_ColumnDef} from 'mantine-react-table';
import type {FC} from 'react';
import {useMemo} from 'react';
import type {
  AssistantToolStatRowResponse,
  AssistantToolStatsResponse,
} from 'src/api/ProactApiClient';
import {assistantToolLabel} from 'src/app/assistant/assistantToolLabels';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {formatMs, formatPercent} from './assistantUsageFormat';
import {AssistantUsageSection} from './AssistantUsageSection';

export interface AssistantToolUseSectionProps {
  stats: AssistantToolStatsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  scopeText: string;
}

const EMPTY_ROWS: AssistantToolStatRowResponse[] = [];

const ToolStatsTable: FC<{rows: AssistantToolStatRowResponse[]}> = ({rows}) => {
  const columns = useMemo<MRT_ColumnDef<AssistantToolStatRowResponse>[]>(
    () => [
      {
        accessorKey: 'toolName',
        header: 'Tool',
        Cell: ({row}) => (
          <Stack gap={0}>
            <Text size="sm">{assistantToolLabel(row.original.toolName)}</Text>
            <Text size="xs" c="dimmed">
              {row.original.toolName}
            </Text>
          </Stack>
        ),
      },
      {accessorKey: 'calls', header: 'Calls'},
      {accessorKey: 'errors', header: 'Errors'},
      {
        accessorKey: 'errorRatePercent',
        header: 'Error Rate',
        Cell: ({row}) => formatPercent(row.original.errorRatePercent),
      },
      {
        accessorKey: 'averageDurationMs',
        header: 'Avg Duration',
        Cell: ({row}) => formatMs(row.original.averageDurationMs),
      },
      {
        accessorKey: 'p95DurationMs',
        header: 'P95 Duration',
        Cell: ({row}) => formatMs(row.original.p95DurationMs),
      },
      {
        accessorKey: 'averageResultChars',
        header: 'Avg Result Size',
        Cell: ({row}) =>
          `${Math.round(row.original.averageResultChars).toLocaleString()} chars`,
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'assistant-tool-stats',
    columns,
    defaultDensity: 'xs',
    defaultSorting: [{id: 'calls', desc: true}],
    defaultPagination: {pageIndex: 0, pageSize: 25},
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableColumnFilters: false,
      enableRowActions: false,
      columns,
      data: rows,
      getRowId: row => row.toolName,
    },
    totalCount: rows.length,
    isError: false,
    isLoading: false,
    isFetching: false,
  });

  return table;
};

/**
 * Control Center tool-use stats: reply-level stat row plus a per-tool table.
 * @registry Scout tool-use stats section (InlineStat row + per-tool useTable).
 */
export const AssistantToolUseSection: FC<AssistantToolUseSectionProps> = ({
  stats,
  isLoading,
  isError,
  scopeText,
}) => {
  const items = stats
    ? [
        {label: 'Replies with tools', value: stats.repliesWithTools.toLocaleString()},
        {label: 'Avg rounds per reply', value: stats.averageRoundsPerReply.toFixed(1)},
        {label: 'Stopped at tool limit', value: formatPercent(stats.toolLimitPercent)},
        {label: 'Tool calls', value: stats.totalCalls.toLocaleString()},
        {label: 'Duplicate calls', value: stats.duplicateCalls.toLocaleString()},
        {label: 'Capped calls', value: stats.cappedCalls.toLocaleString()},
      ]
    : [];

  return (
    <AssistantUsageSection
      title="Tool Use"
      stats={items}
      isLoading={isLoading}
      isError={isError}
      empty={
        !stats || stats.totalCalls === 0 ? (
          <Text size="sm" c="dimmed">
            Scout made no tool calls in {scopeText}.
          </Text>
        ) : undefined
      }>
      <ToolStatsTable rows={stats?.tools ?? EMPTY_ROWS} />
    </AssistantUsageSection>
  );
};
